import React, { useEffect, useRef } from 'react';
import { Box, styled } from '@mui/system';
import { TextField, InputAdornment, IconButton, Tooltip, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { AITypingState } from '../types';

const SearchContainer = styled(Box)<{ isthinking?: string }>(({ isthinking }) => ({
  position: 'relative',
  width: '100%',
  borderRadius: '24px',
  padding: '2px',
  border: '1px solid',
  borderColor: isthinking === 'true' ? '#94a3b8' : '#cbd5e1',
  backgroundColor: '#ffffff',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#94a3b8',
  },
}));

const StyledTextField = styled(TextField)<{ isthinking?: string }>(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '22px',
    paddingLeft: '16px',
    paddingRight: '12px',
    transition: 'all 0.3s ease',
    '& fieldset': {
      border: 'none',
    },
  },
}));

const ShortcutBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f1f5f9',
  border: '1px solid #cbd5e1',
  borderRadius: '4px',
  padding: '2px 6px',
  fontSize: '10px',
  fontWeight: 'bold',
  color: '#64748b',
  marginLeft: '8px',
  userSelect: 'none',
  cursor: 'default',
});

interface AISearchInputProps {
  value: string;
  typingState: AITypingState;
  onChange: (val: string) => void;
  onClear: () => void;
  onForceSearch: () => void;
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
}

export default function AISearchInput({
  value,
  typingState,
  onChange,
  onClear,
  onForceSearch,
  autoFocus,
  onFocus,
  onBlur,
}: AISearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isThinking = typingState === 'thinking' || typingState === 'debouncing';

  // Trigger focus when autoFocus changes
  useEffect(() => {
    if (autoFocus) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  // Global listener for shortcut keys
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Focus on '/' key
      if (e.key === '/' && document.activeElement !== inputRef.current) {
        // Prevent default only if user isn't in another text input
        const tag = document.activeElement?.tagName.toLowerCase();
        if (tag !== 'input' && tag !== 'textarea') {
          e.preventDefault();
          inputRef.current?.focus();
        }
      }

      // Clear/Escape search
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        onClear();
        inputRef.current?.blur();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClear]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onForceSearch();
    }
  };

  // Status indicator text
  let statusText = '';
  let statusColor = 'text.secondary';
  if (typingState === 'typing') {
    statusText = 'Waiting for you to finish typing...';
    statusColor = 'text.secondary';
  } else if (typingState === 'debouncing') {
    statusText = 'AI is preparing to analyze...';
    statusColor = 'primary.main';
  } else if (typingState === 'thinking') {
    statusText = 'AI is analyzing your request...';
    statusColor = 'primary.main';
  }

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <SearchContainer isthinking={isThinking ? 'true' : 'false'}>
        <StyledTextField
          fullWidth
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          inputRef={inputRef}
          isthinking={isThinking ? 'true' : 'false'}
          placeholder="Ask AI anything about your shipments..."
          inputProps={{
            'aria-label': 'Ask AI anything about shipments',
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AutoAwesomeIcon
                  sx={{
                    color: isThinking ? 'primary.main' : 'text.disabled',
                    transition: 'color 0.3s ease',
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {value && (
                  <Tooltip title="Clear Search (Esc)" arrow>
                    <IconButton
                      size="small"
                      onClick={() => {
                        onClear();
                        inputRef.current?.focus();
                      }}
                      sx={{ mr: 0.5 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Trigger Search (Enter)" arrow>
                  <IconButton size="small" onClick={onForceSearch}>
                    <KeyboardReturnIcon fontSize="small" sx={{ color: value ? 'primary.main' : 'text.disabled' }} />
                  </IconButton>
                </Tooltip>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', ml: 1 }}>
                  <ShortcutBadge title="Press '/' to search"> / </ShortcutBadge>
                </Box>
              </InputAdornment>
            ),
          }}
        />
      </SearchContainer>

      {/* Typing states info */}
      {statusText && (
        <Box sx={{ px: 2, mt: 1, display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="caption"
            sx={{
              color: statusColor,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              transition: 'all 0.3s ease',
            }}
          >
            {typingState === 'thinking' && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                }}
              />
            )}
            {statusText}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
